import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  BillEnrollmentResponseSchema,
  ClassSectionListResponseSchema,
  ClassSectionSchema,
  EnrollmentListResponseSchema,
  EnrollmentSchema,
  ProgramListResponseSchema,
  ProgramSchema,
  RosterSchema,
  TermListResponseSchema,
  TermSchema,
  type BillEnrollmentResponse,
  type ClassSection,
  type CreateClassSectionInput,
  type CreateEnrollmentInput,
  type CreateProgramInput,
  type CreateTermInput,
  type Enrollment,
  type EnrollmentStatus,
  type Program,
  type Roster,
  type Term,
  type UpdateClassSectionInput,
  type UpdateEnrollmentInput,
  type UpdateProgramInput,
  type UpdateTermInput,
} from "@/api/types/classRegistration";

/** Query-key factory — mirrors src/api/hooks/useMemberships.ts's convention. */
export const classRegistrationKeys = {
  all: ["class-registration"] as const,
  programs: (activeOnly: boolean) => [...classRegistrationKeys.all, "programs", activeOnly] as const,
  terms: (activeOnly: boolean) => [...classRegistrationKeys.all, "terms", activeOnly] as const,
  sections: (params: Record<string, unknown>) => [...classRegistrationKeys.all, "sections", params] as const,
  sectionsBelowMinimum: (termId: string | undefined) =>
    [...classRegistrationKeys.all, "sections-below-minimum", termId ?? "all"] as const,
  sectionDetail: (id: string | undefined) => [...classRegistrationKeys.all, "section-detail", id] as const,
  roster: (sectionId: string | undefined) => [...classRegistrationKeys.all, "roster", sectionId] as const,
  enrollments: (params: Record<string, unknown>) => [...classRegistrationKeys.all, "enrollments", params] as const,
  enrollmentDetail: (id: string | undefined) => [...classRegistrationKeys.all, "enrollment-detail", id] as const,
  consentAudit: (params: Record<string, unknown>) => [...classRegistrationKeys.all, "consent-audit", params] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: classRegistrationKeys.all });
}

// ---- Programs ------------------------------------------------------------------

export function usePrograms(activeOnly = false) {
  return useQuery({
    queryKey: classRegistrationKeys.programs(activeOnly),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/classes/programs?active_only=${activeOnly}`);
      return validateResponse(ProgramListResponseSchema, data, "GET /v2/classes/programs").items;
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateProgram() {
  const queryClient = useQueryClient();
  return useMutation<Program, unknown, CreateProgramInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/classes/programs", input);
      return validateResponse(ProgramSchema, data, "POST /v2/classes/programs");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateProgram() {
  const queryClient = useQueryClient();
  return useMutation<Program, unknown, UpdateProgramInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/classes/programs/${id}`, patch);
      return validateResponse(ProgramSchema, data, "PATCH /v2/classes/programs/:id");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

// ---- Terms --------------------------------------------------------------------

export function useTerms(activeOnly = false) {
  return useQuery({
    queryKey: classRegistrationKeys.terms(activeOnly),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/classes/terms?active_only=${activeOnly}`);
      return validateResponse(TermListResponseSchema, data, "GET /v2/classes/terms").items;
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateTerm() {
  const queryClient = useQueryClient();
  return useMutation<Term, unknown, CreateTermInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/classes/terms", input);
      return validateResponse(TermSchema, data, "POST /v2/classes/terms");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateTerm() {
  const queryClient = useQueryClient();
  return useMutation<Term, unknown, UpdateTermInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/classes/terms/${id}`, patch);
      return validateResponse(TermSchema, data, "PATCH /v2/classes/terms/:id");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

// ---- Class sections -------------------------------------------------------------

interface UseSectionsParams {
  term_id?: string;
  program_id?: string;
  active_only?: boolean;
}

export function useClassSections(params: UseSectionsParams = {}) {
  const { term_id, program_id, active_only } = params;
  return useQuery({
    queryKey: classRegistrationKeys.sections({ term_id, program_id, active_only }),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (term_id) searchParams.set("term_id", term_id);
      if (program_id) searchParams.set("program_id", program_id);
      if (active_only) searchParams.set("active_only", "true");
      const { data } = await apiClient.get(`/v2/classes/sections?${searchParams.toString()}`);
      return validateResponse(ClassSectionListResponseSchema, data, "GET /v2/classes/sections").items;
    },
    enabled: term_id !== undefined,
    staleTime: 10 * 1000,
  });
}

export function useSectionsBelowMinimum(termId: string | undefined) {
  return useQuery({
    queryKey: classRegistrationKeys.sectionsBelowMinimum(termId),
    queryFn: async () => {
      const qs = termId ? `?term_id=${termId}` : "";
      const { data } = await apiClient.get(`/v2/classes/sections/below-minimum${qs}`);
      return validateResponse(ClassSectionListResponseSchema, data, "GET /v2/classes/sections/below-minimum").items;
    },
    staleTime: 10 * 1000,
  });
}

export function useClassSection(sectionId: string | undefined) {
  return useQuery<ClassSection>({
    queryKey: classRegistrationKeys.sectionDetail(sectionId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/classes/sections/${sectionId}`);
      return validateResponse(ClassSectionSchema, data, "GET /v2/classes/sections/:id");
    },
    enabled: !!sectionId,
  });
}

export function useCreateClassSection() {
  const queryClient = useQueryClient();
  return useMutation<ClassSection, unknown, CreateClassSectionInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/classes/sections", input);
      return validateResponse(ClassSectionSchema, data, "POST /v2/classes/sections");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateClassSection() {
  const queryClient = useQueryClient();
  return useMutation<ClassSection, unknown, UpdateClassSectionInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/classes/sections/${id}`, patch);
      return validateResponse(ClassSectionSchema, data, "PATCH /v2/classes/sections/:id");
    },
    onSuccess: (section) => {
      queryClient.setQueryData(classRegistrationKeys.sectionDetail(section.id), section);
      invalidateAll(queryClient);
    },
  });
}

export function useSectionRoster(sectionId: string | undefined) {
  return useQuery<Roster>({
    queryKey: classRegistrationKeys.roster(sectionId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/classes/sections/${sectionId}/roster`);
      return validateResponse(RosterSchema, data, "GET /v2/classes/sections/:id/roster");
    },
    enabled: !!sectionId,
    staleTime: 10 * 1000,
  });
}

// ---- Enrollments ------------------------------------------------------------------

interface UseEnrollmentsParams {
  section_id?: string;
  guardian_contact_id?: string;
  status?: EnrollmentStatus;
}

export function useEnrollments(params: UseEnrollmentsParams = {}) {
  const { section_id, guardian_contact_id, status } = params;
  return useQuery({
    queryKey: classRegistrationKeys.enrollments({ section_id, guardian_contact_id, status }),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (section_id) searchParams.set("section_id", section_id);
      if (guardian_contact_id) searchParams.set("guardian_contact_id", guardian_contact_id);
      if (status) searchParams.set("status", status);
      const { data } = await apiClient.get(`/v2/classes/enrollments?${searchParams.toString()}`);
      return validateResponse(EnrollmentListResponseSchema, data, "GET /v2/classes/enrollments").items;
    },
    staleTime: 10 * 1000,
  });
}

interface UseConsentAuditParams {
  section_id?: string;
  guardian_contact_id?: string;
  date_from?: string;
  date_to?: string;
}

export function useConsentAudit(params: UseConsentAuditParams = {}) {
  const { section_id, guardian_contact_id, date_from, date_to } = params;
  return useQuery({
    queryKey: classRegistrationKeys.consentAudit({ section_id, guardian_contact_id, date_from, date_to }),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (section_id) searchParams.set("section_id", section_id);
      if (guardian_contact_id) searchParams.set("guardian_contact_id", guardian_contact_id);
      if (date_from) searchParams.set("date_from", date_from);
      if (date_to) searchParams.set("date_to", date_to);
      const { data } = await apiClient.get(`/v2/classes/enrollments/consent-audit?${searchParams.toString()}`);
      return validateResponse(EnrollmentListResponseSchema, data, "GET /v2/classes/enrollments/consent-audit").items;
    },
    staleTime: 30 * 1000,
  });
}

export function useEnrollment(enrollmentId: string | undefined) {
  return useQuery<Enrollment>({
    queryKey: classRegistrationKeys.enrollmentDetail(enrollmentId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/classes/enrollments/${enrollmentId}`);
      return validateResponse(EnrollmentSchema, data, "GET /v2/classes/enrollments/:id");
    },
    enabled: !!enrollmentId,
  });
}

export function useCreateEnrollment() {
  const queryClient = useQueryClient();
  return useMutation<BillEnrollmentResponse, unknown, CreateEnrollmentInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/classes/enrollments", input);
      return validateResponse(BillEnrollmentResponseSchema, data, "POST /v2/classes/enrollments");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateEnrollment() {
  const queryClient = useQueryClient();
  return useMutation<Enrollment, unknown, UpdateEnrollmentInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/classes/enrollments/${id}`, patch);
      return validateResponse(EnrollmentSchema, data, "PATCH /v2/classes/enrollments/:id");
    },
    onSuccess: (enrollment) => {
      queryClient.setQueryData(classRegistrationKeys.enrollmentDetail(enrollment.id), enrollment);
      invalidateAll(queryClient);
    },
  });
}

export function useWithdrawEnrollment() {
  const queryClient = useQueryClient();
  return useMutation<Enrollment, unknown, string>({
    mutationFn: async (enrollmentId) => {
      const { data } = await apiClient.post(`/v2/classes/enrollments/${enrollmentId}/withdraw`, {});
      return validateResponse(EnrollmentSchema, data, "POST /v2/classes/enrollments/:id/withdraw");
    },
    onSuccess: (enrollment) => {
      queryClient.setQueryData(classRegistrationKeys.enrollmentDetail(enrollment.id), enrollment);
      invalidateAll(queryClient);
    },
  });
}

export function usePromoteEnrollment() {
  const queryClient = useQueryClient();
  return useMutation<BillEnrollmentResponse, unknown, string>({
    mutationFn: async (enrollmentId) => {
      const { data } = await apiClient.post(`/v2/classes/enrollments/${enrollmentId}/promote`, {});
      return validateResponse(BillEnrollmentResponseSchema, data, "POST /v2/classes/enrollments/:id/promote");
    },
    onSuccess: (result) => {
      queryClient.setQueryData(classRegistrationKeys.enrollmentDetail(result.enrollment.id), result.enrollment);
      invalidateAll(queryClient);
    },
  });
}

export function useCompleteEnrollment() {
  const queryClient = useQueryClient();
  return useMutation<Enrollment, unknown, string>({
    mutationFn: async (enrollmentId) => {
      const { data } = await apiClient.post(`/v2/classes/enrollments/${enrollmentId}/complete`, {});
      return validateResponse(EnrollmentSchema, data, "POST /v2/classes/enrollments/:id/complete");
    },
    onSuccess: (enrollment) => {
      queryClient.setQueryData(classRegistrationKeys.enrollmentDetail(enrollment.id), enrollment);
      invalidateAll(queryClient);
    },
  });
}
